import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { membershipApplicationAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { FileSpreadsheet, FileText } from 'lucide-react';
import HelpTooltip from '../../components/UI/HelpTooltip';

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmCard = styled.div`
  width: min(480px, 92vw);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 22px 45px -20px rgba(15, 23, 42, 0.45);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ConfirmHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eef2f7;
`;

const ConfirmTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #111827;
`;

const ConfirmBody = styled.div`
  padding: 1rem 1.25rem;
  color: #374151;
  line-height: 1.55;
`;

const ConfirmFooter = styled.div`
  padding: 0.9rem 1.25rem;
  border-top: 1px solid #eef2f7;
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
`;

const ConfirmButton = styled.button`
  padding: 0.55rem 0.95rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border 0.2s, opacity 0.2s;
  ${({ variant }) => {
    switch (variant) {
      case 'danger':
        return `
          background: #dc2626;
          color: #fff;
          &:hover{ background: #b91c1c; }
        `;
      case 'primary':
        return `
          background: #2563eb;
          color: #fff;
          &:hover{ background: #1d4ed8; }
        `;
      default:
        return `
          background: #fff;
          color: #111827;
          border-color: #e5e7eb;
          &:hover{ background: #f9fafb; }
        `;
    }
  }}
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ReasonTextArea = styled.textarea`
  width: 100%;
  min-height: 96px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  font-size: 0.95rem;
  resize: vertical;
  &:focus { outline: none; border-color: #2563eb; }
`;

// NOTE: Export functionality for membership applications has been disabled.
// The previous export field configuration is kept here commented out for reference.
// If export is reintroduced in the future, these can be reused.
// const APPLICATION_EXPORT_FIELDS = [
//   { value: 'id', label: 'Submission ID' },
//   { value: 'registration_no', label: 'Registration No' },
//   { value: 'form', label: 'Form' },
//   { value: 'applicant_name', label: 'Applicant Name' },
//   { value: 'applicant_email', label: 'Applicant Email' },
//   { value: 'applicant_phone', label: 'Applicant Phone' },
//   { value: 'status', label: 'Status' },
//   { value: 'submitted_at', label: 'Submitted At' },
//   { value: 'reviewer', label: 'Reviewed By' },
//   { value: 'approver', label: 'Approved By' },
//   { value: 'committee_notes', label: 'Committee Notes' },
//   { value: 'rejection_reason', label: 'Rejection Reason' },
// ];

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const IconButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const ExportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.9rem;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: white;
  cursor: pointer;
  font-size: 0.9rem;
  color: #1f2933;
  transition: background 0.2s, border 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #c7ccd3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExportModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const ExportModalContent = styled.div`
  width: min(520px, 90vw);
  max-height: 80vh;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 40px -20px rgba(15, 23, 42, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ExportModalHeader = styled.div`
  padding: 1.25rem 1.5rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
`;

const ExportModalTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: #1f2937;
`;

const ExportModalSubtitle = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.9rem;
  color: #64748b;
`;

const ExportModalBody = styled.div`
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
`;

const ExportCheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
`;

const ExportCheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.65rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #1f2937;
  transition: border 0.2s, background 0.2s;

  input {
    width: 18px;
    height: 18px;
  }

  &:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }
`;

const ExportModalFooter = styled.div`
  padding: 1.1rem 1.5rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const ExportFooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  font-size: 0.85rem;
  color: #64748b;
`;

const ExportFooterButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ExportFooterLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.85rem;
  color: #2563eb;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ExportModalButton = styled.button`
  padding: 0.55rem 1.1rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border 0.2s;

  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: #2563eb;
          color: white;
          &:hover:not(:disabled) {
            background: #1d4ed8;
          }
        `;
      case 'secondary':
        return `
          background: white;
          border-color: #cbd5f5;
          color: #1f2937;
          &:hover:not(:disabled) {
            background: #f8fafc;
            border-color: #94a3b8;
          }
        `;
      default:
        return `
          background: white;
          color: #1f2937;
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FilterBar = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  border: 1px solid #e0e0e0;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  
  input {
    width: 100%;
    padding: 0.65rem 0.75rem 0.65rem 2.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.95rem;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
    
    &::placeholder {
      color: #999;
    }
  }
  
  &::before {
    content: '🔍';
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1rem;
  }
`;

const Select = styled.select`
  padding: 0.65rem 2rem 0.65rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  min-width: 180px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 0.95rem;
  padding: 0.5rem 0.75rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #f0f0f0;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: #555;
  white-space: nowrap;
  cursor: ${props => (props.sortable === false ? 'default' : 'pointer')};
  user-select: none;
  
  &:hover {
    color: ${props => (props.sortable === false ? '#555' : props.theme.colors.primary)};
  }
  
  &::after {
    content: ${props => (props.sortable === false ? "''" : "'↕'")};
    margin-left: ${props => (props.sortable === false ? '0' : '0.5rem')};
    opacity: 0.4;
    font-size: 0.75rem;
  }
`;

const TableCell = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.9rem;
  color: #333;
`;

const StatusBadge = styled.span`
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  
  ${props => {
    switch (props.status) {
      case 'submitted':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'under_review':
        return 'background: #fff3e0; color: #f57c00;';
      case 'committee_review':
        return 'background: #fce4ec; color: #c2185b;';
      case 'eligible':
        return 'background: #f3e5f5; color: #7b1fa2;';
      case 'resubmit':
        return 'background: #fff8e1; color: #f9a825;';
      case 'approved':
        return 'background: #e8f5e9; color: #388e3c;';
      case 'rejected':
        return 'background: #ffebee; color: #d32f2f;';
      default:
        return 'background: #f5f5f5; color: #666;';
    }
  }}
`;

const ApplicantCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ApplicantAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
`;

const ApplicantDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  .name {
    font-weight: 600;
    color: #1f2937;
  }

  .meta {
    font-size: 0.8rem;
    color: #6b7280;
  }
`;

const RegistrationTag = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ActionButton = styled.button`
  background: ${props => {
    if (props.variant === 'danger') return '#dc3545';
    if (props.variant === 'secondary') return '#6c757d';
    return props.theme.colors.primary;
  }};
  color: white;
  border: none;
  padding: 0.4rem 0.85rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  margin-right: 0.5rem;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #999;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #ccc;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 0;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
`;

const PaginationInfo = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  background: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? props.theme.colors.primary : '#f8f9fa'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MembershipApplicationSubmissions = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Export-related state removed (export functionality disabled)
  // const [exporting, setExporting] = useState({ excel: false, pdf: false });
  // const [showExportModal, setShowExportModal] = useState(false);
  // const [pendingExportFormat, setPendingExportFormat] = useState(null);
  // const [selectedExportColumns, setSelectedExportColumns] = useState(APPLICATION_EXPORT_FIELDS.map(option => option.value));
  // const [tempExportColumns, setTempExportColumns] = useState(APPLICATION_EXPORT_FIELDS.map(option => option.value));
  const [confirmState, setConfirmState] = useState({ open: false, mode: null, submissionId: null, loading: false, reason: '' });
  const [resubmitState, setResubmitState] = useState({ open: false, submission: null, reason: '', loading: false });

  useEffect(() => {
    fetchSubmissions();
  }, [searchTerm, statusFilter, sectorFilter, sortColumn, sortDirection, currentPage]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      
      const response = await membershipApplicationAPI.getSubmissions({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        sector: sectorFilter || undefined,
        sort_by: sortColumn,
        sort_direction: sortDirection,
        page: currentPage,
        per_page: 20
      });
      
      // Handle both paginated and non-paginated responses
      let submissionsData = response.data.data || response.data;
      const count = response.data.total || (Array.isArray(submissionsData) ? submissionsData.length : 0);
      
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);
      setTotalCount(count);
      setTotalPages(response.data.last_page || Math.ceil(count / 20) || 1);
    } catch (error) {
      console.error('❌ Error fetching submissions:', error);
      toast.error('Failed to load submissions');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const openApproveConfirm = (id) => {
    setConfirmState({ open: true, mode: 'approve', submissionId: id, loading: false, reason: '' });
  };

  const openRejectConfirm = (id) => {
    setConfirmState({ open: true, mode: 'reject', submissionId: id, loading: false, reason: '' });
  };

  const performConfirmAction = async () => {
    const { mode, submissionId, reason } = confirmState;
    if (!submissionId || !mode) return;
    try {
      setConfirmState(prev => ({ ...prev, loading: true }));
      if (mode === 'approve') {
        await membershipApplicationAPI.approveSubmission(submissionId);
        toast.success('Application approved successfully');
      } else if (mode === 'reject') {
        const finalReason = String(reason || '').trim();
        if (!finalReason) {
          toast.error('Rejection reason is required.');
          setConfirmState(prev => ({ ...prev, loading: false }));
          return;
        }
        await membershipApplicationAPI.rejectSubmission(submissionId, finalReason);
        toast.success('Application rejected');
      }
      setConfirmState({ open: false, mode: null, submissionId: null, loading: false, reason: '' });
      fetchSubmissions();
    } catch (error) {
      toast.error(error?.response?.data?.message || `Failed to ${mode} application`);
      setConfirmState(prev => ({ ...prev, loading: false }));
    }
  };

  const closeConfirm = () => {
    if (confirmState.loading) return;
    setConfirmState({ open: false, mode: null, submissionId: null, loading: false, reason: '' });
  };

  const openResubmitConfirm = (submission) => {
    setResubmitState({ 
      open: true, 
      submission, 
      reason: submission?.committee_notes || '', 
      loading: false 
    });
  };

  const closeResubmitConfirm = () => {
    if (resubmitState.loading) return;
    setResubmitState({ open: false, submission: null, reason: '', loading: false });
  };

  const handleRequestResubmit = async () => {
    const { submission, reason } = resubmitState;
    if (!submission) return;

    const finalReason = String(reason || '').trim();
    if (!finalReason) {
      toast.error('Resubmission reason is required.');
      return;
    }

    try {
      setResubmitState(prev => ({ ...prev, loading: true }));
      await membershipApplicationAPI.updateSubmissionStatus(submission.id, {
        status: 'resubmit',
        committee_notes: finalReason,
      });
      toast.success('Resubmission requested. The applicant will be notified.');
      setResubmitState({ open: false, submission: null, reason: '', loading: false });
      fetchSubmissions();
    } catch (error) {
      console.error('Failed to request resubmission:', error);
      const message = error.response?.data?.message || 'Failed to request resubmission.';
      toast.error(message);
      setResubmitState(prev => ({ ...prev, loading: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStatus = (status) => {
    if (status === 'resubmit') {
      return 'Needs Resubmission';
    }
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getInitials = (name = '') => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const renderApplicantCell = (submission) => {
    const phone = submission.form_data?.phone_number || submission.applicant_phone || 'Phone not provided';
    const email = submission.applicant_email || 'Email not provided';
    const name = submission.applicant_name || 'Unnamed Applicant';
    const sector = submission.form_data?.sector || submission.form_data?.occupation_position || submission.form_data?.organization_type;

    return (
      <ApplicantCell>
        <ApplicantAvatar title={name}>
          {getInitials(name)}
        </ApplicantAvatar>
        <ApplicantDetails>
          <span className="name">{name}</span>
          <span className="meta">{email}</span>
          <span className="meta">{phone}</span>
          {sector && <span className="meta">Sector: {sector}</span>}
        </ApplicantDetails>
      </ApplicantCell>
    );
  };

  // Export helper functions removed (export functionality disabled)

  return (
    <Container>
      <Header>
        <Title>
          Application Submissions
          <HelpTooltip>
            <p>This page is used to review membership applications submitted from the public forms.</p>
            <ul>
              <li><strong>Approve</strong>: creates a draft member record and sends the applicant an email with registration steps.</li>
              <li><strong>Reject</strong>: marks the application as rejected and sends the applicant your rejection reason.</li>
              <li><strong>Resubmit</strong>: asks the applicant to update their information using your notes before a decision.</li>
            </ul>
          </HelpTooltip>
        </Title>
        <HeaderActions>
          <IconButton onClick={() => navigate('/app/membership-applications/import')}>
            📥
          </IconButton>
          <IconButton onClick={fetchSubmissions}>
            ↻
          </IconButton>
          <IconButton>
            ⋮
          </IconButton>
        </HeaderActions>
      </Header>

      <FilterBar>
        <SearchInput>
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>

        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Select Status</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="committee_review">Committee Review</option>
          <option value="eligible">Eligible</option>
          <option value="resubmit">Needs Resubmission</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>

        <Select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)}>
          <option value="">Select Sector</option>
          <option value="Government">Government</option>
          <option value="NGO">NGO</option>
          <option value="Civil Society">Civil Society</option>
          <option value="Education">Education</option>
          <option value="Healthcare">Healthcare</option>
          <option value="Legal">Legal</option>
          <option value="Media">Media</option>
          <option value="Private Sector">Private Sector</option>
          <option value="International Organization">International Organization</option>
          <option value="Other">Other</option>
        </Select>

        <FilterLink onClick={() => {
          setSearchTerm('');
          setStatusFilter('');
          setSectorFilter('');
        }}>
          Clear filters
        </FilterLink>

        {/* Export column selection removed (export disabled) */}
      </FilterBar>

      <TableContainer>
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell onClick={() => handleSort('id')}>ID</TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('applicant_name')}>Applicant</TableHeaderCell>
              <TableHeaderCell sortable={false}>Registration</TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('form_id')}>Form</TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('status')}>Status</TableHeaderCell>
              <TableHeaderCell onClick={() => handleSort('submitted_at')}>Submitted</TableHeaderCell>
              <TableHeaderCell sortable={false}>Actions</TableHeaderCell>
            </tr>
          </TableHeader>
          <tbody>
            {loading ? (
              <tr>
                <TableCell colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>
                  Loading...
                </TableCell>
              </tr>
            ) : submissions.length === 0 ? (
              <tr>
                <TableCell colSpan="7">
                  <EmptyState>
                    <EmptyIcon>!</EmptyIcon>
                    <EmptyText>No data available at the moment. Please check back later for updates.</EmptyText>
                  </EmptyState>
                </TableCell>
              </tr>
            ) : (
              submissions.map((submission) => (
                <TableRow key={submission.id}>
                  <TableCell>#{submission.id}</TableCell>
                  <TableCell>{renderApplicantCell(submission)}</TableCell>
                  <TableCell>
                    {submission.registration_no ? (
                      <RegistrationTag>{submission.registration_no}</RegistrationTag>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>Not assigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {submission.form?.title || submission.form?.name || '—'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={submission.status}>
                      {formatStatus(submission.status)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{formatDate(submission.submitted_at || submission.created_at)}</TableCell>
                  <TableCell>
                    <ActionButton onClick={() => navigate(`/app/membership-applications/submissions/${submission.id}`)}>
                      View
                    </ActionButton>
                    {submission.status === 'submitted' && (
                      <>
                        <ActionButton onClick={() => openApproveConfirm(submission.id)}>
                          Approve
                        </ActionButton>
                        <ActionButton variant="danger" onClick={() => openRejectConfirm(submission.id)}>
                          Reject
                        </ActionButton>
                      </>
                    )}
                    {submission.status !== 'approved' && submission.status !== 'rejected' && (
                      <ActionButton
                        variant="secondary"
                        onClick={() => openResubmitConfirm(submission)}
                      >
                        Resubmit
                      </ActionButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </Table>

        {submissions.length > 0 && (
          <Pagination>
            <PaginationInfo>
              Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} entries
            </PaginationInfo>
            <PaginationButtons>
              <PageButton
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              {[...Array(totalPages)].map((_, i) => (
                <PageButton
                  key={i + 1}
                  active={currentPage === i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </PageButton>
              ))}
              <PageButton
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
            </PaginationButtons>
          </Pagination>
        )}
      </TableContainer>
      {/* Export modal removed (export functionality disabled) */}
      {confirmState.open && (
        <ConfirmOverlay>
          <ConfirmCard>
            <ConfirmHeader>
              <ConfirmTitle>
                {confirmState.mode === 'approve' ? 'Approve Application' : 'Reject Application'}
              </ConfirmTitle>
            </ConfirmHeader>
            <ConfirmBody>
              {confirmState.mode === 'approve' ? (
                <p>Are you sure you want to approve this application? The applicant will receive an approval email with next steps.</p>
              ) : (
                <>
                  <p>Please provide a reason for rejection. This will be shared with the applicant.</p>
                  <ReasonTextArea
                    value={confirmState.reason}
                    onChange={(e) => setConfirmState(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Enter rejection reason..."
                  />
                </>
              )}
            </ConfirmBody>
            <ConfirmFooter>
              <ConfirmButton type="button" onClick={closeConfirm} disabled={confirmState.loading}>
                Cancel
              </ConfirmButton>
              <ConfirmButton
                type="button"
                variant={confirmState.mode === 'reject' ? 'danger' : 'primary'}
                onClick={performConfirmAction}
                disabled={confirmState.loading}
              >
                {confirmState.loading
                  ? (confirmState.mode === 'reject' ? 'Rejecting...' : 'Approving...')
                  : (confirmState.mode === 'reject' ? 'Reject' : 'Approve')}
              </ConfirmButton>
            </ConfirmFooter>
          </ConfirmCard>
        </ConfirmOverlay>
      )}
      {resubmitState.open && (
        <ConfirmOverlay>
          <ConfirmCard>
            <ConfirmHeader>
              <ConfirmTitle>Request Resubmission</ConfirmTitle>
            </ConfirmHeader>
            <ConfirmBody>
              <p style={{ marginBottom: '1rem' }}>
                Enter the details the applicant needs to address before resubmitting. This will be shared with the applicant.
              </p>
              {resubmitState.submission && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  <strong>Applicant:</strong> {resubmitState.submission.applicant_name || 'Unknown'}<br />
                  <strong>Email:</strong> {resubmitState.submission.applicant_email || 'N/A'}
                </div>
              )}
              <ReasonTextArea
                value={resubmitState.reason}
                onChange={(e) => setResubmitState(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Enter the details the applicant needs to address before resubmitting..."
                style={{ minHeight: '120px' }}
              />
            </ConfirmBody>
            <ConfirmFooter>
              <ConfirmButton type="button" onClick={closeResubmitConfirm} disabled={resubmitState.loading}>
                Cancel
              </ConfirmButton>
              <ConfirmButton
                type="button"
                variant="primary"
                onClick={handleRequestResubmit}
                disabled={resubmitState.loading || !resubmitState.reason.trim()}
              >
                {resubmitState.loading ? 'Requesting...' : 'Request Resubmission'}
              </ConfirmButton>
            </ConfirmFooter>
          </ConfirmCard>
        </ConfirmOverlay>
      )}
    </Container>
  );
};

export default MembershipApplicationSubmissions;
